import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import './VerificationResults.css';
import { ReactComponent as AppleIcon } from './apple.svg';
import { ReactComponent as AndroidIcon } from './android.svg';
import { ACTIVE_BRAND, buildDigitalCardRequest } from './brandingConfig';

function VerificationResults({ 
  verificationId,
  livenessResults, 
  documentData, 
  faceCompareResults,
  signatureResults,
  referenceImage,
  frontImage,
  backImage,
  onReset 
}) {
  
  const [digitalCard, setDigitalCard] = useState(null);
  const [loadingCard, setLoadingCard] = useState(false);
  
  // Debug logging
  console.log('VerificationResults - documentData:', documentData);
  console.log('VerificationResults - backAddress:', documentData?.backAddress);
  
  const isVerificationSuccessful = () => {
    const livenessPass = livenessResults?.status === 'SUCCEEDED' && livenessResults?.confidence >= 50;
    const faceMatchPass = faceCompareResults?.matched && faceCompareResults?.similarity >= 70;
    const documentValid = documentData && !documentData.error;
    
    return livenessPass && faceMatchPass && documentValid;
  };

  const isExpired = () => {
    if (!documentData?.expiryDate) return false;
    const expiryDate = new Date(documentData.expiryDate);
    return expiryDate < new Date();
  };

  const isLoading = () => {
    return !documentData || !faceCompareResults;
  };

  const getVerificationStatus = () => {
    // Show loading status if data isn't ready yet
    if (isLoading()) {
      return {
        text: 'PROCESSING',
        class: 'processing',
        title: 'Verification In Progress',
        message: 'Processing verification data...'
      };
    }

    const expired = isExpired();
    const livenessSkipped = livenessResults?.skipped;
    const faceMatchSkipped = faceCompareResults?.skipped;
    const faceMatchFailed = !faceCompareResults?.matched || faceCompareResults?.similarity < 70;
    
    // If liveness was skipped, we only check document validity and expiry
    if (livenessSkipped || faceMatchSkipped) {
      if (expired) {
        return { 
          text: 'EXPIRED', 
          class: 'expired',
          title: 'License Expired', 
          message: 'License has expired.' 
        };
      }
      return { 
        text: 'CLASSIFIED', 
        class: 'warning',
        title: 'Document Classified', 
        message: 'Document validated successfully (Liveness verification skipped).' 
      };
    }
    
    // Normal flow with liveness
    if (faceMatchFailed && expired) {
      return { 
        text: 'FAILED', 
        class: 'failed', 
        title: 'Verification Failed',
        message: 'Face comparison failed and license has expired.' 
      };
    }
    if (faceMatchFailed) {
      return { 
        text: 'FAILED', 
        class: 'failed',
        title: 'Verification Failed', 
        message: 'Selfie does not match ID photo.' 
      };
    }
    if (expired) {
      return { 
        text: 'EXPIRED', 
        class: 'expired',
        title: 'License Expired', 
        message: 'License has expired.' 
      };
    }
    if (!isVerificationSuccessful()) {
      return { 
        text: 'FAILED', 
        class: 'failed',
        title: 'Verification Failed', 
        message: 'Verification checks did not pass.' 
      };
    }
    return { 
        text: 'VERIFIED', 
      class: 'success',
      title: 'Verification Successful', 
      message: 'All checks passed successfully.' 
    };
  };

  const status = getVerificationStatus();

  // Create digital card as soon as firstName and lastName are available
  useEffect(() => {
    const createDigitalCard = async () => {
      // Extract first and last name from documentData
      const firstName = documentData?.fullName?.split(' ')[0] || documentData?.firstName || '';
      const lastName = documentData?.fullName?.split(' ').slice(-1)[0] || documentData?.lastName || '';
      
      console.log('Digital Card Check:', {
        hasFirstName: !!firstName,
        hasLastName: !!lastName,
        hasDigitalCard: !!digitalCard,
        isLoadingCard: loadingCard
      });
      
      // Create card as soon as we have first and last name
      const shouldCreateCard = firstName 
        && lastName 
        && !digitalCard 
        && !loadingCard;
      
      if (shouldCreateCard) {
        console.log('Creating digital card with:', { firstName, lastName });
        setLoadingCard(true);
        
        try {
          // TODO: Replace with your actual API Gateway URL
          const API_URL = 'https://u6hh8u4hjl.execute-api.us-east-1.amazonaws.com/default/createDigitalCard';
          
          // DEMO MODE: Show sample QR codes if API is not configured
          if (API_URL.includes('YOUR_API_GATEWAY_URL')) {
            // Simulate API delay
            await new Promise(resolve => setTimeout(resolve, 1500));
            
            // Set demo data
            setDigitalCard({
              success: true,
              cardId: 'demo-' + Date.now(),
              appleWalletUrl: 'https://example.com/apple-wallet',
              googleWalletUrl: 'https://example.com/google-wallet',
              // Sample QR code base64 (small demo QR)
              appleQR: 'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==',
              googleQR: 'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=='
            });
            setLoadingCard(false);
            return;
          }
          
          // Build the card request using branding configuration
          const cardRequest = buildDigitalCardRequest({
            firstName: firstName,
            lastName: lastName,
            email: documentData?.email,
            phone: documentData?.phone,
            licenceNumber: documentData?.licenceNumber,
            selfieImage: referenceImage,
            dateOfBirth: documentData?.dateOfBirth
          });
          
          console.log(`Creating ${ACTIVE_BRAND.displayName} digital card:`, cardRequest);
          
          const response = await fetch(API_URL, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(cardRequest)
          });

          const data = await response.json();
          console.log('Digital card API response:', data);
          
          // Check if we have valid wallet URLs (regardless of success flag)
          if (data.appleWalletUrl && data.googleWalletUrl) {
            setDigitalCard(data);
          } else {
            console.error('Failed to create digital card:', data.error || data.message || 'Unknown error');
            console.log('Full response:', data);
            // Fallback to demo mode if API fails
            console.log('Using demo QR codes as fallback');
            setDigitalCard({
              success: true,
              cardId: 'demo-fallback-' + Date.now(),
              appleWalletUrl: 'https://example.com/apple-wallet',
              googleWalletUrl: 'https://example.com/google-wallet',
              appleQR: null, // Will show link instead
              googleQR: null // Will show link instead
            });
          }
        } catch (error) {
          console.error('Error creating digital card:', error);
          // Fallback to demo mode on error
          console.log('Using demo QR codes due to error');
          setDigitalCard({
            success: true,
            cardId: 'demo-error-' + Date.now(),
            appleWalletUrl: 'https://example.com/apple-wallet',
            googleWalletUrl: 'https://example.com/google-wallet',
            appleQR: null,
            googleQR: null
          });
        } finally {
          setLoadingCard(false);
        }
      }
    };

    createDigitalCard();
  }, [documentData, digitalCard, loadingCard]);

  return (
    <div className="verification-results-modal">
      {/* Header */}
      <div className="vr-header">
        <div>
          <h2>Verification Details</h2>
          <p className="vr-id">ID #{verificationId?.substring(0, 8) || '79496585'}</p>
        </div>
        <button onClick={onReset} className="vr-close">×</button>
      </div>

      {/* Status Banner */}
      <div className={`vr-status-banner ${status.class}`}>
        <div className="vr-status-icon">
          {status.class === 'success' && '✓'}
          {status.class === 'warning' && '!'}
          {status.class === 'failed' && '✗'}
          {status.class === 'expired' && '⏰'}
          {status.class === 'processing' && <div className="spinner-small"></div>}
        </div>
        <div className="vr-status-content">
          <div className="vr-status-header">
            <span className="vr-status-title">{status.title}</span>
            <span className={`vr-status-pill ${status.class}`}>{status.text}</span>
          </div>
          <p className="vr-status-message">{status.message}</p>
        </div>
      </div>

      {/* Submitted Images */}
      <div className="vr-section">
        <h3>SUBMITTED IMAGES</h3>
        <div className="vr-images-grid">
          <div className="vr-image-item">
            <div className="vr-image-label">
              <span className="vr-icon">👤</span>
              Selfie
            </div>
            <div className="vr-image-box">
              {referenceImage ? (
                <img src={`data:image/jpeg;base64,${referenceImage}`} alt="Selfie" />
              ) : (
                <div className="vr-image-placeholder">
                  <span className="vr-placeholder-icon">👤</span>
                  {livenessResults?.skipped && (
                    <p className="vr-skipped-text">Skipped</p>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="vr-image-item">
            <div className="vr-image-label">
              <span className="vr-icon">🪪</span>
              ID Portrait
            </div>
            <div className="vr-image-box">
              {faceCompareResults?.portraitImage ? (
                <img src={`data:image/jpeg;base64,${faceCompareResults.portraitImage}`} alt="ID Portrait" />
              ) : faceCompareResults ? (
                <div className="vr-image-placeholder">
                  <span className="vr-placeholder-icon">🪪</span>
                  <p className="vr-skipped-text">Not Available</p>
                </div>
              ) : (
                <div className="vr-image-placeholder">
                  <div className="spinner-small"></div>
                  <p className="vr-skipped-text">Loading...</p>
                </div>
              )}
            </div>
          </div>

          <div className="vr-image-item">
            <div className="vr-image-label">
              <span className="vr-icon">💳</span>
              ID Front
            </div>
            <div className="vr-image-box">
              {frontImage ? (
                <img src={frontImage.startsWith('data:') ? frontImage : `data:image/jpeg;base64,${frontImage}`} alt="ID Front" />
              ) : (
                <div className="vr-image-placeholder">
                  <span className="vr-placeholder-icon">💳</span>
                </div>
              )}
            </div>
          </div>

          <div className="vr-image-item">
            <div className="vr-image-label">
              <span className="vr-icon">💳</span>
              ID Back
            </div>
            <div className="vr-image-box">
              {backImage ? (
                <img src={backImage.startsWith('data:') ? backImage : `data:image/jpeg;base64,${backImage}`} alt="ID Back" />
              ) : (
                <div className="vr-image-placeholder">
                  <span className="vr-placeholder-icon">💳</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Personal Information */}
      <div className="vr-section">
        <h3>PERSONAL INFORMATION</h3>
        {!documentData ? (
          <div className="vr-loading">
            <div className="spinner"></div>
            <p>Loading document data...</p>
          </div>
        ) : documentData.error ? (
          <div className="vr-error">
            <p>❌ {documentData.error}</p>
          </div>
        ) : (
          <div className="vr-info-grid">
            {documentData.fullName && (
              <>
                <div className="vr-info-item">
                  <label>FIRST NAME</label>
                  <p>{documentData.fullName.split(' ')[0]}</p>
                </div>
                {documentData.fullName.split(' ').length > 2 && (
                  <div className="vr-info-item">
                    <label>MIDDLE NAME</label>
                    <p>{documentData.fullName.split(' ').slice(1, -1).join(' ')}</p>
                  </div>
                )}
                <div className="vr-info-item">
                  <label>LAST NAME</label>
                  <p>{documentData.fullName.split(' ').slice(-1)[0]}</p>
                </div>
              </>
            )}
            
            {documentData.dateOfBirth && (
              <div className="vr-info-item">
                <label>📅 DATE OF BIRTH</label>
                <p>{documentData.dateOfBirth}</p>
              </div>
            )}
            
            {documentData.documentNumber && (
              <div className="vr-info-item">
                <label>🪪 LICENCE NUMBER</label>
                <p>{documentData.documentNumber}</p>
              </div>
            )}
            
            {documentData.expiryDate && (
              <div className="vr-info-item">
                <label>⏰ LICENCE EXPIRY</label>
                <p className={isExpired() ? 'vr-expired-text' : ''}>
                  {documentData.expiryDate}
                  {isExpired() && <span className="vr-expired-badge">EXPIRED</span>}
                </p>
              </div>
            )}
            
            {documentData.country && (
              <div className="vr-info-item">
                <label>🌍 COUNTRY NAME</label>
                <p>{documentData.country}</p>
              </div>
            )}

            {documentData.address && (
              <div className="vr-info-item vr-full-width">
                <label>📍 ADDRESS</label>
                <p>{documentData.address}</p>
              </div>
            )}

            {documentData.backAddress && (
              <div className="vr-info-item vr-full-width vr-highlight">
                <label>📍 CHANGE OF ADDRESS LABEL</label>
                <p>{documentData.backAddress}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Biometric Verification */}
      <div className="vr-section">
        <h3>BIOMETRIC VERIFICATION</h3>
        
        {/* Face Compare */}
        {!faceCompareResults ? (
          <div className="vr-loading-card">
            <div className="spinner-small"></div>
            <p>Analyzing face match...</p>
          </div>
        ) : (
          <div className={`vr-verification-card ${faceCompareResults.skipped ? 'warning' : (faceCompareResults.matched && faceCompareResults.similarity >= 70 ? 'success' : 'failed')}`}>
            <div className="vr-verification-icon">
              {faceCompareResults.skipped ? '!' : (faceCompareResults.matched && faceCompareResults.similarity >= 70 ? '✓' : '✗')}
            </div>
            <div className="vr-verification-content">
              <div className="vr-verification-header">
                <span className="vr-verification-title">Face Compare</span>
                {faceCompareResults.skipped ? (
                  <span className="vr-status-pill warning">Skipped</span>
                ) : null}
              </div>
              <p className="vr-verification-message">
                {faceCompareResults.skipped
                  ? 'Face comparison was skipped (no selfie available)'
                  : (faceCompareResults.matched && faceCompareResults.similarity >= 70
                      ? 'Selfie matches ID photo'
                      : 'Selfie does not match ID photo')
                }
              </p>
              
              {/* Match Details - Front and Back Percentages */}
              {!faceCompareResults.skipped && (
                <div className="vr-match-details">
                  {faceCompareResults.frontMatch && (
                    <div className="vr-match-item">
                      <span className="vr-match-label">Front ID Match:</span>
                      <span className={`vr-match-value ${faceCompareResults.frontMatch.similarity >= 70 ? 'match-pass' : 'match-fail'}`}>
                        {faceCompareResults.frontMatch.similarity.toFixed(1)}%
                      </span>
                    </div>
                  )}
                  {faceCompareResults.backMatch && faceCompareResults.backMatch.similarity > 0 && (
                    <div className="vr-match-item">
                      <span className="vr-match-label">Back ID Match:</span>
                      <span className={`vr-match-value ${faceCompareResults.backMatch.similarity >= 70 ? 'match-pass' : 'match-fail'}`}>
                        {faceCompareResults.backMatch.similarity.toFixed(1)}%
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Liveness Check */}
        {livenessResults && (
          <div className={`vr-verification-card ${livenessResults.skipped ? 'warning' : (livenessResults.status === 'SUCCEEDED' && livenessResults.confidence >= 50 ? 'success' : 'failed')}`}>
            <div className="vr-verification-icon">
              {livenessResults.skipped ? '!' : (livenessResults.status === 'SUCCEEDED' && livenessResults.confidence >= 50 ? '✓' : '✗')}
            </div>
            <div className="vr-verification-content">
              <div className="vr-verification-header">
                <span className="vr-verification-title">Liveness Detection</span>
                {livenessResults.skipped ? (
                  <span className="vr-status-pill warning">Skipped</span>
                ) : (
                  <span className={`vr-confidence-badge ${livenessResults.status === 'SUCCEEDED' && livenessResults.confidence >= 50 ? 'success' : 'failed'}`}>
                    {livenessResults.confidence ? `${livenessResults.confidence.toFixed(1)}%` : 'N/A'}
                  </span>
                )}
              </div>
              <p className="vr-verification-message">
                {livenessResults.skipped 
                  ? (livenessResults.skipReason || 'Liveness verification was skipped')
                  : (livenessResults.status === 'SUCCEEDED' && livenessResults.confidence >= 50
                      ? 'Real person detected'
                      : 'Spoofed - Liveness check failed')
                }
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Signature Detection */}
      <div className="vr-section">
        <h3>SIGNATURE DETECTION</h3>
        {!signatureResults ? (
          <div className="vr-loading-card">
            <div className="spinner-small"></div>
            <p>Detecting signatures...</p>
          </div>
        ) : (
          <div className={`vr-verification-card ${signatureResults.hasSignature ? 'success' : 'failed'}`}>
            <div className="vr-verification-icon">
              {signatureResults.hasSignature ? '✓' : '✗'}
            </div>
            <div className="vr-verification-content">
              <div className="vr-verification-header">
                <span className="vr-verification-title">Signature Presence</span>
                <span className={`vr-confidence-badge ${signatureResults.hasSignature ? 'success' : 'failed'}`}>
                  {signatureResults.totalSignatures} Signature{signatureResults.totalSignatures !== 1 ? 's' : ''}
                </span>
              </div>
              <p className="vr-verification-message">
                {signatureResults.hasSignature
                  ? `${signatureResults.totalSignatures} signature(s) detected on ID`
                  : 'No signature detected on ID'
                }
              </p>
              
              {/* Signature Details */}
              <div className="vr-match-details">
                {signatureResults.frontSignature?.detected && (
                  <div className="vr-match-item">
                    <span className="vr-match-label">Front ID Signature:</span>
                    <span className="vr-match-value match-pass">
                      {signatureResults.frontSignature.confidence.toFixed(1)}%
                    </span>
                  </div>
                )}
                {signatureResults.backSignature?.detected && (
                  <div className="vr-match-item">
                    <span className="vr-match-label">Back ID Signature:</span>
                    <span className="vr-match-value match-pass">
                      {signatureResults.backSignature.confidence.toFixed(1)}%
                    </span>
                  </div>
                )}
              </div>
              
              {/* Signature Images */}
              <div className="vr-signature-images">
                {signatureResults.frontSignature?.detected && signatureResults.frontSignature.signatureBase64 && (
                  <div className="vr-signature-box">
                    <div className="vr-signature-label">📝 Front Signature</div>
                    <img 
                      src={`data:image/jpeg;base64,${signatureResults.frontSignature.signatureBase64}`} 
                      alt="Front Signature"
                      className="vr-signature-img"
                    />
                    <div className="vr-signature-confidence">
                      {signatureResults.frontSignature.confidence.toFixed(1)}% confidence
                    </div>
                  </div>
                )}
                {signatureResults.backSignature?.detected && signatureResults.backSignature.signatureBase64 && (
                  <div className="vr-signature-box">
                    <div className="vr-signature-label">📝 Back Signature</div>
                    <img 
                      src={`data:image/jpeg;base64,${signatureResults.backSignature.signatureBase64}`} 
                      alt="Back Signature"
                      className="vr-signature-img"
                    />
                    <div className="vr-signature-confidence">
                      {signatureResults.backSignature.confidence.toFixed(1)}% confidence
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Digital Card QR Codes */}
      {(digitalCard || loadingCard) && (
        <div className="vr-section">
          <div className="vr-card">
            <h3>ADD TO DIGITAL WALLET</h3>
            
            {loadingCard ? (
              <div className="vr-loading-card">
                <div className="spinner-small"></div>
                <p>Generating digital card...</p>
              </div>
            ) : digitalCard && (
              <>
                <p className="vr-digital-card-description">
                  <span className="desktop-only">Scan the QR code below to add your verified ID to your mobile wallet</span>
                  <span className="mobile-only">Tap the button below to add your verified ID to your mobile wallet</span>
                </p>
                
                <div className="vr-qr-codes-container">
                  {digitalCard.appleWalletUrl && (
                    <div className="vr-qr-code-box">
                      <div className="vr-qr-header">
                        <AppleIcon className="vr-wallet-icon" />
                        <span className="vr-wallet-label">Apple Wallet</span>
                      </div>
                      <div className="vr-qr-code">
                        <QRCodeSVG 
                          value={digitalCard.appleWalletUrl}
                          size={200}
                          level="M"
                          includeMargin={true}
                        />
                      </div>
                      <a 
                        href={digitalCard.appleWalletUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="vr-wallet-link"
                      >
                        Add to Apple Wallet
                      </a>
                    </div>
                  )}
                  
                  {digitalCard.googleWalletUrl && (
                    <div className="vr-qr-code-box">
                      <div className="vr-qr-header">
                        <AndroidIcon className="vr-wallet-icon" />
                        <span className="vr-wallet-label">Google Wallet</span>
                      </div>
                      <div className="vr-qr-code">
                        <QRCodeSVG 
                          value={digitalCard.googleWalletUrl}
                          size={200}
                          level="M"
                          includeMargin={true}
                        />
                      </div>
                      <a 
                        href={digitalCard.googleWalletUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="vr-wallet-link"
                      >
                        Add to Google Wallet
                      </a>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="vr-actions">
        <button onClick={onReset} className="vr-button-primary">
          Verify Another Person
        </button>
      </div>
    </div>
  );
}

export default VerificationResults;